import _extends from "@babel/runtime/helpers/extends";
/*
 *                     NOTICE
 *            COPYRIGHT 2021 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import * as AbReactResources from "abreact/generated/locale/AbReactResources";
import { ReactUtils } from "abreact/incubator/ui/utils/ReactUtils";
import { AbIcon } from "abreact/misc/AbIcon";
import { AbSize, AbSizeContext, sizeProviderWrapper } from "abreact/misc/AbSize";
import { AbWithTooltip } from "abreact/popup/AbWithTooltip";
import * as AbHooks from "abreact/utils/AbHooks";
import * as React from "react";
const calcTargetObj = props => {
  switch (true) {
    // openInNewTab takes precedence over below props.target reasoning
    case props.openInNewTab:
      return {
        target: "_blank",
        rel: "noreferrer"
      };

    // _self case: open in current browser tab
    case !props.target || props.target === "_self":
      return {};
    // anchor target value defaults to _self, so don't pass it (less noise)

    // _blank or named browser tab
    case isOpenInNewTab(props):
      return {
        target: props.target,
        rel: "noreferrer"
      };

    // Remaining builtin target values (_parent, _top, _unfencedTop) are all frame-related
    // (and within current browser tab), which are rare/deprecated/discouraged.  But just in case...
    default:
      return {
        target: props.target
      };
  }
};
const BuiltinTargetValues = new Set(["_self", "_blank", "_parent", "_top", "_unfencedTop"]);
export const isOpenInNewTab = props => {
  switch (true) {
    case props.openInNewTab || props.target === "_blank":
      return true;
    // _blank case
    case !props.target || BuiltinTargetValues.has(props.target):
      return false;
    // all builtins other than _blank are in existing browser tab
    default:
      // target must be a named browser tab (e.g., https://usefulangle.com/post/337/html-open-links-in-same-tab)
      return true;
  }
};
export const AbLink = props => {
  const contextSize = React.useContext(AbSizeContext);
  const uniqueId = AbHooks.useUniqueId("ab-button");
  const linkRef = React.useRef(null);
  AbHooks.useOnMount(() => {
    props.autoFocus && linkRef.current?.focus();
  });
  const onClick = props.onClick ? event => {
    // let modifier-key clicks get handled in the normal way to support link shortcut behavior
    if ((event.altKey || event.shiftKey || event.ctrlKey || event.metaKey) && props.href) return;
    if (props.onClick) {
      props.onClick(event);
      event.stopPropagation();
      event.preventDefault();
    }
  } : undefined;
  const newTabProps = calcTargetObj(props);
  const size = props.size ?? contextSize;
  const externalLinkIconSize = calculateExternalLinkIconSize(size ?? AbSize.MEDIUM);
  const {
    disabled,
    ...inputProps
  } = ReactUtils.inputProps("ab-link", props, ["abui", size, props.noWrap && "ab-no-wrap", !!(props.icon && !props.label) && "ab-link-icon-only"]);
  const calcHref = () => {
    if (disabled) return undefined;
    if (props.href) return props.href;
    if (onClick) return "#"; // ONLY OK because event.preventDefault is called in onClick. Allows ENTER to trigger onClick.
    return undefined;
  };
  const inner = /*#__PURE__*/React.createElement(AbWithTooltip, _extends({
    clippedTrigger: linkRef
  }, AbWithTooltip.makeClippedProps(props.tooltip, props.label)), /*#__PURE__*/React.createElement("a", _extends({}, inputProps, props.__htmlProps, {
    id: props.id ?? uniqueId,
    href: calcHref(),
    download: "download" in props && props.download
  }, newTabProps, {
    ref: linkRef,
    onClick: disabled ? undefined : onClick
  }), props.icon && /*#__PURE__*/React.createElement(React.Fragment, null, typeof props.icon === "string" ? /*#__PURE__*/React.createElement(AbIcon, {
    image: props.icon,
    size: size
  }) : props.icon, props.label && /*#__PURE__*/React.createElement(React.Fragment, null, "\xA0")), /*#__PURE__*/React.createElement("span", {
    className: "ab-link-label-wrapper"
  }, props.label), isOpenInNewTab(props) && /*#__PURE__*/React.createElement(React.Fragment, null, "\xA0", /*#__PURE__*/React.createElement(AbIcon, {
    altText: AbReactResources.abLink_opensInANewWindowIcon_altText(),
    className: "ab-external-link-icon",
    image: "absdk:external-link",
    size: externalLinkIconSize
  }))));
  return sizeProviderWrapper(inner, props, contextSize);
};

/**
 * Utility function to return the next smaller icon size for the external link.
 */
export const calculateExternalLinkIconSize = size => {
  switch (size) {
    case AbSize.EXTRA_SMALL:
      return AbSize.EXTRA_SMALL;
    case AbSize.SMALL:
      return AbSize.EXTRA_SMALL;
    case AbSize.MEDIUM:
      return AbSize.EXTRA_SMALL;
    /* Return extra small, because small icons are the same size as medium. */
    case AbSize.LARGE:
      return AbSize.MEDIUM;
    case AbSize.EXTRA_LARGE:
      return AbSize.LARGE;
    case AbSize.CUSTOM:
      return AbSize.CUSTOM;
  }
};
//# sourceMappingURL=AbLink.js.map
